import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { Tabs } from '@heroui/react';
import { DateRangeField } from '@/components/dateRangeField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { LinkedTab } from '../../tabHref';
import type { StatementsOutletContext } from './statementsOutletContext';
import type { StatementsTab, StatementsTabId } from './statementsTabsMeta';

type StatementsPageViewProps = {
    tabs: LinkedTab<StatementsTab>[];
    selectedTabId: StatementsTabId;
    fromDate: string;
    toDate: string;
    onRangeChange: (fromDate: string, toDate: string) => void;
    /** Handed to the open tab through the outlet — the shell's date range. */
    outletContext: StatementsOutletContext;
};

/**
 * Statements shell — render only. Same shape as Books: the range sits above the
 * tab bar because it scopes both tabs, and each tab's content is the routed
 * `<Outlet />`.
 *
 * Only the selected panel mounts, so exactly one child route renders — the tab
 * bar and the URL can't disagree.
 */
export const StatementsPageView: React.FC<StatementsPageViewProps> = ({
    tabs,
    selectedTabId,
    fromDate,
    toDate,
    onRangeChange,
    outletContext,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('accounting.statements.title')}
            className="flex w-full flex-col gap-5"
            {...getNodeTestIdProp('statements-section')}
        >
            <div className="w-full min-w-52 max-w-72">
                <DateRangeField
                    ariaLabel={t('accounting.statements.rangeAria')}
                    fromDate={fromDate}
                    onChange={onRangeChange}
                    slot="statements"
                    toDate={toDate}
                />
            </div>
            {/* No `onSelectionChange`: each tab is a real link now, and
                React Aria does not report the clicked key through it for link
                tabs — it fires with the key already selected, or not at all.
                Wiring it to a navigate would send the user to the tab they
                were already on. The URL is the single source of truth and
                `selectedKey` reads it. */}

            <Tabs
                className="w-full"
                selectedKey={selectedTabId}
                variant="secondary"
            >
                <Tabs.ListContainer className="min-w-0 max-w-max">
                    <Tabs.List aria-label={t('accounting.statements.tabsAria')}>
                        {tabs.map((tab) => (
                            // `.tabs__tab` is `w-full` with no nowrap, so on a
                            // narrow screen the labels share the width and wrap
                            // mid-name. Sized to their text instead, they
                            // overflow — which is what the list container's
                            // scroller is for.
                            <Tabs.Tab
                                className="whitespace-nowrap"
                                href={tab.href}
                                id={tab.id}
                                key={tab.id}
                                {...getNodeTestIdProp(
                                    `statements-tab-${tab.id}`,
                                )}
                            >
                                {t(tab.labelKey)}
                                <Tabs.Indicator />
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>
                </Tabs.ListContainer>
                {tabs.map((tab) => (
                    <Tabs.Panel
                        className="pt-4"
                        id={tab.id}
                        key={tab.id}
                        {...getNodeTestIdProp(`statements-panel-${tab.id}`)}
                    >
                        <Outlet context={outletContext} />
                    </Tabs.Panel>
                ))}
            </Tabs>
        </section>
    );
};
